import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';

import { PlusIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { MAX_AUTO_VEHICLES } from 'lib/autoTransport/autoVehicleTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { useSuperDispatchVehicleCatalog } from '../../hooks/useSuperDispatchVehicleCatalog';
import { AutoTrailerType, QuickQuoteInputs } from '../../types';
import { emptyAutoTransportVehicle } from '../autoTransportVehicleHelpers';
import { SUPER_DISPATCH_VEHICLE_CATALOG_LOAD_ERROR_MESSAGE } from '../superDispatchVehicleCatalog';
import { AutoTransportFieldError } from './AutoTransportFieldError';
import { AutoVehicleFields } from './AutoVehicleFields';
import { SegmentedToggle, SegmentedToggleOption } from './SegmentedToggle';

const TRAILER_TYPE_OPTIONS: readonly [
  SegmentedToggleOption<AutoTrailerType>,
  SegmentedToggleOption<AutoTrailerType>,
] = [
  { value: AutoTrailerType.Open, label: 'Open', ariaLabel: 'Open trailer' },
  {
    value: AutoTrailerType.Enclosed,
    label: 'Enclosed',
    ariaLabel: 'Enclosed trailer',
  },
];

type AutoTransportFieldsProps = {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
};

export function AutoTransportFields({ formMethods }: AutoTransportFieldsProps) {
  const {
    control,
    getValues,
    formState: { errors },
  } = formMethods;

  const { loadError: catalogLoadError, retryLoad: retryCatalogLoad } =
    useSuperDispatchVehicleCatalog();

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'autoTransport.vehicles',
  });

  // Copy the vehicle's current form values so edits made since the last render
  // carry over, and place the copy right below it.
  const duplicateVehicle = (index: number) => {
    const source = getValues(`autoTransport.vehicles.${index}`);
    insert(index + 1, { ...emptyAutoTransportVehicle(), ...source });
  };

  return (
    <Flex
      direction='col'
      gap='lg'
      className='w-full min-w-0 max-w-full'
      data-testid='quick-quote-auto-transport-fields'
    >
      <Flex direction='col' gap='xs' className='w-full min-w-0'>
        <Label name='autoTransport.trailerType' required>
          Trailer Type
        </Label>
        <Controller
          name='autoTransport.trailerType'
          control={control}
          render={({ field }) => (
            <SegmentedToggle
              value={field.value ?? AutoTrailerType.Open}
              options={TRAILER_TYPE_OPTIONS}
              onChange={field.onChange}
            />
          )}
        />
        <AutoTransportFieldError
          errors={errors}
          name='autoTransport.trailerType'
        />
      </Flex>

      {catalogLoadError && (
        <Flex align='center' justify='between' gap='sm' className='w-full'>
          <Typography variant='body-xs' className='text-error'>
            {SUPER_DISPATCH_VEHICLE_CATALOG_LOAD_ERROR_MESSAGE}
          </Typography>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='h-7 shrink-0 px-2 text-xs'
            buttonNamePosthog={
              ButtonNamePosthog.QuickQuoteAutoRetryVehicleCatalog
            }
            onClick={retryCatalogLoad}
          >
            Retry
          </Button>
        </Flex>
      )}

      <Flex direction='col' gap='sm' className='w-full min-w-0'>
        {fields.map((field, index) => (
          <AutoVehicleFields
            key={field.id}
            formMethods={formMethods}
            index={index}
            vehicleCount={fields.length}
            canRemove={fields.length > 1}
            onRemove={() => remove(index)}
            canDuplicate={fields.length < MAX_AUTO_VEHICLES}
            onDuplicate={() => duplicateVehicle(index)}
          />
        ))}
      </Flex>

      {fields.length < MAX_AUTO_VEHICLES && (
        <Flex justify='center' className='w-full'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='w-fit h-7 px-2.5 text-xs text-neutral-800 hover:border-neutral-700 hover:bg-neutral-100'
            buttonNamePosthog={ButtonNamePosthog.QuickQuoteAutoAddVehicle}
            onClick={() => append(emptyAutoTransportVehicle())}
          >
            <PlusIcon className='mr-1 h-3.5 w-3.5' aria-hidden />
            Add Vehicle
          </Button>
        </Flex>
      )}
      <AutoTransportFieldError errors={errors} name='autoTransport.vehicles' />
    </Flex>
  );
}
